import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Target, Share2, Copy, Check } from 'lucide-react';

const GOD_COLORS = {
  athena: 'text-amber-400', hermes: 'text-orange-400', apollo: 'text-yellow-400',
  eris: 'text-red-400', themis: 'text-blue-400', calliope: 'text-pink-400',
  hestia: 'text-orange-300', plutus: 'text-green-400', nike: 'text-yellow-400', metis: 'text-indigo-400',
};

const CONSENSUS_STYLES = {
  strong: { label: 'Strong Consensus', color: 'text-green-400', bg: 'bg-green-500/10', border: 'border-green-500/30' },
  moderate: { label: 'Moderate Consensus', color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/30' },
  divided: { label: 'Council Divided', color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/30' },
};

const VerdictPanel = ({ verdict, onSave, onNewSession, onShare, shareUrl }) => {
  const [copied, setCopied] = useState(false);

  if (!verdict) return null;

  const consensusStyle = CONSENSUS_STYLES[verdict.consensus_level] || CONSENSUS_STYLES.moderate;

  const copyShareLink = () => {
    if (shareUrl) {
      const fullUrl = process.env.REACT_APP_BACKEND_URL + shareUrl;
      navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="space-y-4 animate-fade-in" data-testid="verdict-panel">
      {/* Consensus Badge & Summary */}
      <div className={`${consensusStyle.bg} border ${consensusStyle.border} rounded-xl p-4`}>
        <div className="flex items-center gap-2 mb-3">
          <CheckCircle className={`w-5 h-5 ${consensusStyle.color}`} />
          <span className={`text-sm font-bold ${consensusStyle.color}`}>{consensusStyle.label}</span>
        </div>
        <p className="text-gray-200 text-sm leading-relaxed font-medium">{verdict.summary}</p>
      </div>

      {/* Priority Recommendations */}
      {verdict.recommendations && verdict.recommendations.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400 font-semibold uppercase tracking-wider px-1 flex items-center gap-2">
            <Target className="w-3.5 h-3.5" /> Priority Actions
          </p>
          {verdict.recommendations.map((rec, i) => (
            <div
              key={i}
              className="bg-gray-800/30 border border-gray-700/40 rounded-xl p-3 flex gap-3 animate-fade-in"
              style={{ animationDelay: `${i * 100}ms` }}
              data-testid={`recommendation-${i}`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                i === 0 ? 'bg-green-500/20 text-green-400' : i === 1 ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700/50 text-gray-400'
              }`}>
                <span className="text-xs font-bold">P{rec.priority || i + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-200 font-medium leading-snug">{rec.action}</p>
                <p className="text-xs text-gray-500 mt-1">{rec.rationale}</p>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {rec.owner_emoji && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <span>{rec.owner_emoji}</span>
                      <span className={GOD_COLORS[rec.owner_god] || 'text-gray-400'}>{rec.owner_name}</span>
                    </span>
                  )}
                  {rec.metric_to_track && (
                    <span className="text-[10px] px-2 py-0.5 bg-gray-700/50 text-gray-400 rounded-full">
                      Track: {rec.metric_to_track}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Risks */}
      {verdict.risks && verdict.risks.length > 0 && (
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-3">
          <p className="text-xs text-orange-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Key Risks
          </p>
          <ul className="space-y-1">
            {verdict.risks.map((risk, i) => (
              <li key={i} className="text-xs text-gray-400 flex items-start gap-2">
                <span className="text-orange-500/60 mt-0.5">-</span>
                <span>{risk}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Dissenting Opinion */}
      {verdict.dissenting_opinion && (
        <div className="bg-red-900/10 border border-red-500/20 rounded-xl p-3">
          <p className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-2 flex items-center gap-2">
            <span>⚔️</span> Devil's Advocate — Unresolved Concern
          </p>
          <p className="text-xs text-gray-400 leading-relaxed italic">"{verdict.dissenting_opinion}"</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-1">
        <button
          onClick={onShare}
          disabled={!!shareUrl}
          className="flex-1 py-2.5 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 disabled:from-gray-700 disabled:to-gray-700 text-white font-bold rounded-xl transition-all text-sm flex items-center justify-center gap-2"
          data-testid="share-verdict-btn"
        >
          {shareUrl ? (
            <span onClick={(e) => { e.stopPropagation(); copyShareLink(); }} className="flex items-center gap-2 cursor-pointer">
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copied ? 'Link Copied!' : 'Copy Share Link'}
            </span>
          ) : (
            <><Share2 className="w-4 h-4" /> Share Brief</>
          )}
        </button>
        <button
          onClick={onSave}
          className="py-2.5 px-4 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-all text-sm"
          data-testid="save-verdict-btn"
        >
          Save
        </button>
        <button
          onClick={onNewSession}
          className="py-2.5 px-4 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-xl transition-all text-sm"
          data-testid="new-session-btn"
        >
          New
        </button>
      </div>
    </div>
  );
};

export default VerdictPanel;
